<template>
  <div>
    <Head :title="$t('message.loan.create_loan')" />
    <h1 class="mb-8 text-3xl font-bold">
      <Link class="text-indigo-400 hover:text-indigo-600" href="/loans/loans">
        {{ $t('message.loan.loan') }}
      </Link>
      <span class="text-indigo-400 font-medium">/</span> {{ $t('message.general.create') }}
    </h1>

    <!-- Eligibility Information -->
    <div class="bg-blue-50 border-blue-200 mb-6 p-4 border rounded-md">
      <h2 class="text-blue-800 mb-2 text-lg font-semibold">Loan Eligibility Requirements</h2>
      <ul class="text-blue-700 pl-5 list-disc">
        <li>You must have at least 3 years of service with Nafa to be eligible for a loan.</li>
        <li>Monthly loan repayment cannot exceed 40% of your net salary.</li>
        <li>Loan should be repaid within 24 months of disbursement.</li>
      </ul>
    </div>

    <!-- Form Validation Note -->
    <div class="bg-amber-50 border-amber-200 mb-6 p-4 border rounded-md">
      <h3 class="text-amber-800 text-md mb-2 font-semibold">📋 Form Completion Order</h3>
      <p class="text-amber-700 text-sm">Please complete the fields marked with <span class="font-bold">*</span> in the following order:</p>
      <ol class="text-amber-700 mt-2 pl-5 text-sm list-decimal">
        <li>Select Loan Type</li>
        <li>Enter Net Salary</li>
        <li>Enter Loan Duration</li>
        <li>Then enter your desired Loan Amount</li>
      </ol>
    </div>

    <!-- Error message for eligibility -->
    <div v-if="$page.props.errors.eligibility" class="mb-6 p-4 bg-red-100 border border-red-300 rounded-md">
      <p class="text-red-700">{{ $page.props.errors.eligibility }}</p>
    </div>

    <div class="max-w-3xl bg-white rounded-md shadow overflow-hidden">
      <form @submit.prevent="store">
        <div class="flex flex-wrap -mb-8 -mr-6 p-8">
          <!-- Loan Type -->
          <select-input v-model="form.loan_type_id" :error="form.errors.loan_type_id" class="pb-8 pr-6 w-full lg:w-1/2" :label="$t('message.loan.loan_type') + ' *'" @change="updateLoanLimits">
            <option :value="null" />
            <option v-for="loanType in loan_types" :key="loanType.id" :value="loanType.id">{{ loanType.name }} ({{ loanType.interest_rate }}%)</option>
          </select-input>

          <!-- Branch -->
          <select-input v-model="form.branch" :error="form.errors.branch" class="pb-8 pr-6 w-full lg:w-1/2" :label="$t('message.branches.branch')">
            <option :value="null" />
            <option v-for="branch in branches" :key="branch.id" :value="branch.id">{{ branch.name }}</option>
          </select-input>

          <!-- Level -->
          <select-input v-model="form.level" :error="form.errors.level" class="pb-8 pr-6 w-full lg:w-1/2" :label="$t('message.staffs.level')">
            <option :value="null" />
            <option v-for="level in levels" :key="level.id" :value="level.id">
              {{ level.name }}
            </option>
          </select-input>

          <!-- Net Salary -->
          <text-input v-model="form.net_salary" :error="form.errors.net_salary" class="pb-8 pr-6 w-full lg:w-1/2" :label="$t('message.staffs.net_salary') + ' *'" type="number" min="0" step="0.01" @input="updateMaxEligibleAmount" />

          <!-- Loan Tenure -->
          <text-input v-model="form.loan_tenure" :error="form.errors.loan_tenure" class="pb-8 pr-6 w-full lg:w-1/2" :label="$t('message.loan.duration') + ' *'" type="number" min="1" max="60" @input="updateMaxEligibleAmount" />

          <!-- Loan Amount -->
          <text-input v-model="form.loan_amount" :error="form.errors.loan_amount" class="pb-8 pr-6 w-full lg:w-1/2" :label="$t('message.loan.amount')" type="number" step="0.01" :min="minLoanAmount" :max="maxLoanAmount" :disabled="!canEnterLoanAmount" :placeholder="loanAmountPlaceholder" />

          <!-- Validation message for loan amount -->
          <div v-if="!canEnterLoanAmount" class="flex items-center pb-8 pr-6 w-full lg:w-1/2">
            <div class="text-amber-600 bg-amber-50 border-amber-200 p-3 text-sm border rounded-md">
              <p class="font-medium">Please fill in Net Salary and Duration first</p>
              <p class="mt-1 text-xs">This ensures accurate loan calculations and eligibility checks.</p>
            </div>
          </div>

          <!-- Wallet Number -->
          <text-input v-model="form.wallet_number" :error="form.errors.wallet_number" class="pb-8 pr-6 w-full" :label="$t('message.loan.wallet_number')" type="text" maxlength="7" pattern="[0-9]{7}" title="Wallet number must be exactly 7 digits" @input="validateWalletNumber" />

          <!-- Purpose -->
          <textarea-input v-model="form.purpose" :error="form.errors.purpose" class="pb-8 pr-6 w-full" :label="$t('message.loan.purpose')" />

          <!-- Other Loans with Nafa -->
          <select-input v-model="form.other_loans_with_nafa_id" :error="form.errors.other_loans_with_nafa_id" class="pb-8 pr-6 w-full lg:w-1/2" :label="$t('message.loan.other_loans_with_nafa')">
            <option :value="null">None</option>
            <option v-for="loanType in loan_types" :key="loanType.id" :value="loanType.id">{{ loanType.name }}</option>
          </select-input>

          <!-- Outstanding Nafa Loan Balance -->
          <text-input v-model="form.outstanding_nafa_loan_balance" :error="form.errors.outstanding_nafa_loan_balance" class="pb-8 pr-6 w-full lg:w-1/2" :label="$t('message.loan.outstanding_nafa_loan_balance')" type="number" min="0" step="0.01" />

          <!-- Outstanding Bank Loan Balance -->
          <text-input v-model="form.outstanding_bank_loan_balance" :error="form.errors.outstanding_bank_loan_balance" class="pb-8 pr-6 w-full lg:w-1/2" :label="$t('message.loan.outstanding_bank_loan_balance')" type="number" min="0" step="0.01" />

          <!-- Other Loans with Bank -->
          <text-input v-model="form.other_loans_with_bank" :error="form.errors.other_loans_with_bank" class="pb-8 pr-6 w-full lg:w-1/2" :label="$t('message.loan.other_loans_with_bank')" />

          <!-- Loan Calculation Summary -->
          <div v-if="canCalculatePayment" class="pb-8 pr-6 w-full">
            <h3 class="mb-2 text-lg font-bold">{{ $t('message.loan.payment_summary') }}</h3>
            <div class="p-4 bg-gray-50 rounded-md">
              <div class="grid gap-4 grid-cols-1 md:grid-cols-2">
                <div>
                  <p class="text-gray-600">{{ $t('message.loan.monthly_payment') }}:</p>
                  <p class="font-semibold">{{ formatCurrency(monthlyPayment) }}</p>
                </div>
                <div>
                  <p class="text-gray-600">{{ $t('message.loan.total_payment') }}:</p>
                  <p class="font-semibold">{{ formatCurrency(totalPayment) }}</p>
                </div>
                <div>
                  <p class="text-gray-600">{{ $t('message.loan.total_interest') }}:</p>
                  <p class="font-semibold">{{ formatCurrency(totalInterest) }}</p>
                </div>
                <div>
                  <p class="text-gray-600">{{ $t('message.loan.interest_rate') }}:</p>
                  <p class="font-semibold">{{ selectedLoanType ? selectedLoanType.interest_rate : 0 }}%</p>
                </div>
              </div>

              <!-- Repayment percentage warning -->
              <div v-if="repaymentPercentage > 40" class="mt-4 p-3 text-red-700 bg-red-100 rounded-md">
                <p class="font-semibold">Warning: Monthly repayment is {{ Math.round(repaymentPercentage) }}% of your net salary.</p>
                <p>Loans with monthly repayments exceeding 40% of your net salary will not be approved.</p>
                <p class="mt-1">Maximum eligible loan amount: {{ formatCurrency(maxEligibleLoanAmount) }}</p>
              </div>
              <div v-else-if="repaymentPercentage > 0" class="mt-4 p-3 text-green-700 bg-green-100 rounded-md">
                <p class="font-semibold">Monthly repayment is {{ Math.round(repaymentPercentage) }}% of your net salary.</p>
                <p v-if="repaymentPercentage > 30 && repaymentPercentage <= 40" class="mt-1">You're approaching the 40% limit. Consider a longer tenure or lower loan amount.</p>
              </div>
            </div>
          </div>
        </div>
        <div class="flex items-center justify-end px-8 py-4 bg-gray-50 border-t border-gray-100">
          <loading-button :loading="form.processing" class="btn-indigo" type="submit"> {{ $t('message.loan.apply') }} </loading-button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Layout from '@/Shared/Layout'
import TextInput from '@/Shared/TextInput'
import TextareaInput from '@/Shared/TextareaInput'
import SelectInput from '@/Shared/SelectInput'
import LoadingButton from '@/Shared/LoadingButton'

export default {
  components: {
    Head,
    Link,
    LoadingButton,
    SelectInput,
    TextInput,
    TextareaInput,
  },
  layout: Layout,
  props: {
    loan_types: Array,
    levels: Array,
    branches: Array,
  },
  remember: 'form',
  data() {
    return {
      form: this.$inertia.form({
        loan_type_id: null,
        branch: null,
        level: null,
        net_salary: 0,
        loan_amount: 0,
        loan_tenure: 12,
        purpose: '',
        outstanding_nafa_loan_balance: 0,
        outstanding_bank_loan_balance: 0,
        other_loans_with_nafa_id: null,
        other_loans_with_bank: '',
        wallet_number: '',
        // funds_transfer_status: 'pending',
        // funds_received_at: null,
      }),
      minLoanAmount: 0,
      maxLoanAmount: 0,
      maxEligibleLoanAmount: 0,
      canEnterLoanAmount: false,
      loanAmountPlaceholder: '',
    }
  },
  computed: {
    selectedLoanType() {
      if (!this.form.loan_type_id) return null
      return this.loan_types.find((lt) => lt.id === this.form.loan_type_id)
    },
    canCalculatePayment() {
      return this.form.loan_type_id && this.form.loan_amount > 0 && this.form.loan_tenure > 0
    },
    canEnterLoanAmount() {
      return this.form.net_salary > 0 && this.form.loan_tenure > 0 && this.form.loan_type_id
    },
    loanAmountPlaceholder() {
      if (!this.form.net_salary || this.form.net_salary <= 0) {
        return 'Enter net salary first'
      }
      if (!this.form.loan_tenure || this.form.loan_tenure <= 0) {
        return 'Enter loan duration first'
      }
      if (!this.form.loan_type_id) {
        return 'Select loan type first'
      }
      return `Enter amount between ${this.formatCurrency(this.minLoanAmount)} and ${this.formatCurrency(this.maxLoanAmount)}`
    },
    monthlyPayment() {
      if (!this.canCalculatePayment) return 0

      const principal = parseFloat(this.form.loan_amount)
      const rate = this.selectedLoanType.interest_rate / 100 / 12 // Monthly interest rate
      const term = parseInt(this.form.loan_tenure) // Term in months

      // If interest rate is 0, simple division
      if (rate === 0) {
        return principal / term
      }

      // PMT formula: PMT = P * (r(1+r)^n) / ((1+r)^n - 1)
      const monthlyPayment = (principal * (rate * Math.pow(1 + rate, term))) / (Math.pow(1 + rate, term) - 1)

      return Math.round(monthlyPayment * 100) / 100
    },
    totalPayment() {
      return Math.round(this.monthlyPayment * this.form.loan_tenure * 100) / 100
    },
    totalInterest() {
      return Math.round((this.totalPayment - this.form.loan_amount) * 100) / 100
    },
    repaymentPercentage() {
      if (!this.canCalculatePayment || this.form.net_salary <= 0) return 0
      return (this.monthlyPayment / this.form.net_salary) * 100
    },
  },
  watch: {
    'form.net_salary'(newValue) {
      if (!this.canEnterLoanAmount && this.form.loan_amount > 0) {
        this.form.loan_amount = 0
      }
    },
    'form.loan_tenure'(newValue) {
      if (!this.canEnterLoanAmount && this.form.loan_amount > 0) {
        this.form.loan_amount = 0
      }
    },
    'form.loan_type_id'(newValue) {
      if (!this.canEnterLoanAmount && this.form.loan_amount > 0) {
        this.form.loan_amount = 0
      }
    },
  },
  methods: {
    updateLoanLimits() {
      if (this.selectedLoanType) {
        this.minLoanAmount = this.selectedLoanType.min_loan_amount
        this.maxLoanAmount = this.selectedLoanType.max_loan_amount

        // Clear loan amount if required fields are not filled
        if (!this.canEnterLoanAmount) {
          this.form.loan_amount = 0
        } else {
          // Adjust loan amount if outside limits
          if (this.form.loan_amount < this.minLoanAmount) {
            this.form.loan_amount = this.minLoanAmount
          } else if (this.form.loan_amount > this.maxLoanAmount) {
            this.form.loan_amount = this.maxLoanAmount
          }
        }

        this.updateMaxEligibleAmount()
      } else {
        this.minLoanAmount = 0
        this.maxLoanAmount = 0
        this.maxEligibleLoanAmount = 0
        this.form.loan_amount = 0
      }
    },
    updateMaxEligibleAmount() {
      if (!this.selectedLoanType || this.form.net_salary <= 0 || this.form.loan_tenure <= 0) {
        this.maxEligibleLoanAmount = 0
        // Clear loan amount if required fields are not filled
        if (!this.canEnterLoanAmount) {
          this.form.loan_amount = 0
        }
        return
      }

      const netSalary = parseFloat(this.form.net_salary)
      const maxMonthlyPayment = netSalary * 0.4
      const rate = this.selectedLoanType.interest_rate / 100 / 12 // Monthly interest rate
      const term = parseInt(this.form.loan_tenure)

      // If interest rate is 0, simple multiplication
      if (rate === 0) {
        this.maxEligibleLoanAmount = maxMonthlyPayment * term
      } else {
        // Reverse PMT formula: P = PMT * ((1+r)^n - 1) / (r(1+r)^n)
        this.maxEligibleLoanAmount = (maxMonthlyPayment * (Math.pow(1 + rate, term) - 1)) / (rate * Math.pow(1 + rate, term))
      }

      this.maxEligibleLoanAmount = Math.round(this.maxEligibleLoanAmount * 100) / 100

      // Cap the max eligible amount to the loan type's max amount
      if (this.selectedLoanType && this.maxEligibleLoanAmount > this.selectedLoanType.max_loan_amount) {
        this.maxEligibleLoanAmount = this.selectedLoanType.max_loan_amount
      }
    },
    formatCurrency(amount) {
      return new Intl.NumberFormat('en-GM', {
        style: 'currency',
        currency: 'GMD',
      }).format(amount)
    },
    validateWalletNumber() {
      // Remove any non-digit characters
      this.form.wallet_number = this.form.wallet_number.replace(/\D/g, '')

      // Ensure it's not longer than 7 digits
      if (this.form.wallet_number.length > 7) {
        this.form.wallet_number = this.form.wallet_number.substring(0, 7)
      }
    },
    store() {
      this.form.post('/loans/loans')
    },
  },
}
</script>
